/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  AccountPayoutInitial,
  AccountPayoutRequest,
  AccountPayoutResponse,
  AccountRevenueTotal,
} from '../../interface/account-management-payout.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import accountApi from '../../utils/api/account';

import { setNotificationMessage } from './NotificationManagementSlice';

export const fetchPayout = createAsyncThunk<AccountPayoutResponse, AccountPayoutRequest>(
  'payout/fetchPayout',
  async (data: AccountPayoutRequest, { dispatch }) => {
    const { accountCustomId, ...query } = data;
    if (!accountCustomId) {
      throw new Error('accountId is required');
    }
    try {
      const response = await accountApi.fetchPayoutListReports(
        accountCustomId,
        removeEmptyKeys(query),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

export const fetchAccountRevenueTotal = createAsyncThunk(
  'payout/fetchAccountRevenueTotal',
  async (data: AccountRevenueTotal, { dispatch }) => {
    const { accountCustomId, ...query } = data;
    if (!accountCustomId) {
      throw new Error('accountId is required');
    }
    try {
      const response = await accountApi.fetchAccountRevenueTotal(
        accountCustomId,
        removeEmptyKeys(query),
      );
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

// a thunk to fetch devices filters
export const fetchAccountPayoutFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('payout/fetchAccountPayoutFilters', async (_, { dispatch }) => {
  try {
    const response = await accountApi.fetchPayoutListFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const payoutFilter: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

const initialState: AccountPayoutInitial = {
  payout: [],
  revenueTotal: null,
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: payoutFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

const Payout = createSlice({
  name: 'payout',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param state
     * @param action
     */
    updateAccountPayoutFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param state
     */
    disableAccountPayoutFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
    /**
     * Clear all Data and reset related state
     */
    clearPayoutData: (state) => {
      Object.assign(state, initialState);
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<AccountPayoutInitial>) => {
    builder
      .addCase(fetchPayout.pending, (state: AccountPayoutInitial) => {
        state.loading = true;
      })
      .addCase(
        fetchPayout.fulfilled,
        (state: AccountPayoutInitial, action: PayloadAction<AccountPayoutResponse>) => {
          state.loading = false;
          state.payout = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchPayout.rejected, (state: AccountPayoutInitial) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchAccountRevenueTotal.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchAccountRevenueTotal.fulfilled,
        (state, action: PayloadAction<AccountRevenueTotal>) => {
          state.loading = false;
          state.revenueTotal = action.payload;
        },
      )

      .addCase(fetchAccountRevenueTotal.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(fetchAccountPayoutFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchAccountPayoutFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchAccountPayoutFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const { updateAccountPayoutFilters, disableAccountPayoutFilterApplied, setSortData ,clearPayoutData} =
  Payout.actions;

export default Payout.reducer;
